// Daily cap, same rule as services/whatsapp/wa_core/{limiter,ledger}.py: distinct phones whose ledger row's
// window time (window_at, else sent_at, else last_attempt_at) is within the rolling 24h. window_at is the send
// time, moved to Meta's delivery time by the webhook (Meta's slot runs 24h from delivery).
// A phone already counted costs nothing more. Meta does not count 131026 (not on WhatsApp) once a wamid
// confirms it, nor 131048 (refused: the limit was full) at all. Anything else counts (conservative).
import type { Conversation, LedgerRow } from "@/data/types";
import { DAY_MS } from "@/lib/fields";

export interface CapUsage {
  used: number;
  cap: number;
  left: number;
  /** phones counted in the window */
  phones: ReadonlySet<string>;
  /** when the oldest counted attempt leaves the window (a slot frees), or null */
  freesAt: number | null;
}

/** Over Meta's 24h limit (client.LIMIT_CODES). Never auto-retried; "Retry all" releases them. */
export const LIMIT_CODES: ReadonlySet<number> = new Set([131048]);
/** Meta does not count these toward the 24h limit. Mirrors client.NOT_COUNTED_BY_META_CODES. */
export const NOT_COUNTED_BY_META_CODES: ReadonlySet<number> = new Set([131026, ...LIMIT_CODES]);

/** Same rule as ledger.counts_toward_cap. */
export function countsTowardCap(r: LedgerRow): boolean {
  if (r.status !== "failed" && r.status !== "failed_final") return true;
  const code = Number(r.error?.code);
  if (LIMIT_CODES.has(code)) return false;
  return !(!!r.wamid && NOT_COUNTED_BY_META_CODES.has(code));
}

/** Same rule as ledger.window_time: when this row's cap slot started. */
export function windowTime(r: LedgerRow): number | null {
  return r.windowAt ?? r.sentAt ?? r.lastAttemptAt;
}

/** Same rule as ledger.is_released: wa_retry released it; the next send run sends it first. */
export function isReleased(r: LedgerRow): boolean {
  return r.status === "failed" && !r.wamid && r.retryApprovedAt != null;
}

/** Same rule as ledger.is_over_limit: in the retry queue (failed with 131048, not released yet). */
export function isOverLimit(r: LedgerRow): boolean {
  return r.status === "failed" && LIMIT_CODES.has(Number(r.error?.code)) && !isReleased(r);
}

export function capUsage(ledger: LedgerRow[], cap: number, now: number): CapUsage {
  const since = now - DAY_MS;
  const latestByPhone = new Map<string, number>();
  for (const r of ledger) {
    const t = windowTime(r);
    if (!r.phone || t == null || t < since || !countsTowardCap(r)) continue;
    const prev = latestByPhone.get(r.phone);
    // a phone stays counted until its LATEST window time leaves the window
    if (prev == null || t > prev) latestByPhone.set(r.phone, t);
  }
  const times = [...latestByPhone.values()];
  return {
    used: latestByPhone.size,
    cap,
    left: Math.max(cap - latestByPhone.size, 0),
    phones: new Set(latestByPhone.keys()),
    freesAt: times.length ? Math.min(...times) + DAY_MS : null,
  };
}

/** Reply window: last inbound + 24h (store.window_seconds_left). <= 0 means closed. */
export function windowLeft(conv: Conversation | undefined, now: number): number {
  if (!conv?.lastInboundAt) return 0;
  return conv.lastInboundAt + DAY_MS - now;
}

export function windowOpen(conv: Conversation | undefined, optedOut: boolean, now: number): boolean {
  return !!conv && !optedOut && windowLeft(conv, now) > 0;
}

export type CapLevel = "ok" | "warn" | "full";

/** Colour state of a cap meter: < 80% ok, 80% to < 100% warn, at or over the cap full.
 *  An unknown cap (0) reads ok: nothing to alarm about. */
export function capLevel(used: number, cap: number): CapLevel {
  if (cap <= 0) return "ok";
  if (used >= cap) return "full";
  return used / cap >= 0.8 ? "warn" : "ok";
}
